import React, { useState, useEffect, useMemo, useCallback } from 'react';
import DOMPurify from 'dompurify';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Avatar,
  Divider,
  useColorModeValue,
  Icon,
  Tooltip,
  IconButton,
  Button,
  Spinner,
  Center,
  Select,
  Collapse,
  Wrap,
  WrapItem,
  useToast,
} from '@chakra-ui/react';
import {
  FiPhone,
  FiMail,
  FiMessageSquare,
  FiEdit2,
  FiCheckCircle,
  FiAlertCircle,
  FiUser,
  FiCalendar,
  FiInfo,
  FiCopy,
  FiChevronDown,
  FiChevronUp,
  FiCornerUpLeft,
  FiCornerUpRight,
  FiAlertTriangle,
  FiFileText,
  FiXCircle,
  FiUserX,
  FiPrinter,
  FiCpu,
} from 'react-icons/fi';
import { supabase } from '../../utils/supabase';
import EmailTrackingBadge from './Email/EmailTrackingBadge';
import TranscriptDisclosure from './Dialpad/TranscriptDisclosure';
import { STAGES } from '../../constants/crmConstants';
import moment from 'moment';

function stripHtml(html) {
  if (!html) return '';
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

const ACTIVITY_ICONS = {
  disposition: FiPhone,
  stage_change: FiCheckCircle,
  edit: FiEdit2,
  update: FiAlertCircle,
  created: FiUser,
  note: FiFileText,
  call_summary: FiCpu,
  email_sent: FiMail,
  email: FiMail,
  sms_sent: FiMessageSquare,
  meeting_scheduled: FiCalendar,
  meeting_rescheduled: FiCalendar,
  meeting_cancelled: FiXCircle,
  meeting_no_show: FiUserX,
  auto_update: FiEdit2,
  transfer: FiUser,
  conversion: FiCheckCircle,
  party_added: FiUser,
  party_removed: FiUser,
  dialpad_call: FiPhone,
  dialpad_sms: FiMessageSquare,
  sms_failed: FiAlertTriangle,
  email_failed: FiAlertTriangle,
  email_unsubscribed: FiUserX,
  notification: FiInfo,
  enrichment: FiInfo,
  postcard_sent: FiPrinter,
  postcard_failed: FiAlertTriangle,
};

const ACTIVITY_COLORS = {
  disposition: 'blue',
  stage_change: 'green',
  edit: 'yellow',
  update: 'purple',
  created: 'cyan',
  note: 'yellow',
  call_summary: 'cyan',
  email_sent: 'purple',
  email: 'purple',
  sms_sent: 'teal',
  meeting_scheduled: 'orange',
  meeting_rescheduled: 'orange',
  meeting_cancelled: 'red',
  meeting_no_show: 'red',
  auto_update: 'gray',
  transfer: 'orange',
  conversion: 'green',
  party_added: 'blue',
  party_removed: 'red',
  dialpad_call: 'blue',
  dialpad_sms: 'green',
  sms_failed: 'red',
  email_failed: 'red',
  email_unsubscribed: 'red',
  notification: 'gray',
  enrichment: 'gray',
  postcard_sent: 'green',
  postcard_failed: 'red',
};

/**
 * Filterable categories. Every activity type maps to exactly one category;
 * unknown/new types fall back to the "system" bucket so they never vanish.
 */
export const ACTIVITY_CATEGORIES = [
  { key: 'notes', label: 'Notes & updates', types: ['note', 'call_summary', 'update'] },
  { key: 'dispositions', label: 'Dispositions', types: ['disposition'] },
  { key: 'emails', label: 'Emails', types: ['email_sent', 'email', 'email_failed', 'email_unsubscribed'] },
  { key: 'sms', label: 'Texts', types: ['sms_sent', 'dialpad_sms', 'sms_failed'] },
  { key: 'calls', label: 'Calls', types: ['dialpad_call'] },
  { key: 'meetings', label: 'Meetings', types: ['meeting_scheduled', 'meeting_rescheduled', 'meeting_cancelled', 'meeting_no_show'] },
  { key: 'stages', label: 'Stages & transfers', types: ['stage_change', 'transfer', 'conversion'] },
  { key: 'system', label: 'System & automations', types: ['auto_update', 'created', 'edit', 'notification', 'enrichment', 'party_added', 'party_removed', 'postcard_sent', 'postcard_failed'] },
];

const TYPE_TO_CATEGORY = ACTIVITY_CATEGORIES.reduce((map, cat) => {
  cat.types.forEach((t) => { map[t] = cat.key; });
  return map;
}, {});

const ALL_CATEGORY_KEYS = ACTIVITY_CATEGORIES.map((c) => c.key);
const ALL_SOURCE_KEYS = ['primary', 'deal', 'contact'];

function categoryForType(type) {
  return TYPE_TO_CATEGORY[type] || 'system';
}

function isInboundSmsActivity(activity) {
  const type = activity.activityType || activity.type;
  if (type !== 'dialpad_sms' && type !== 'sms_sent') return false;
  if (activity.direction === 'inbound' || activity.direction === 'incoming') return true;
  const text = activity.text || activity.content || '';
  return /^Inbound SMS/i.test(text);
}

function formatCallDuration(durationSec) {
  const sec = Number(durationSec);
  if (!Number.isFinite(sec) || sec <= 0) return null;
  const minutes = Math.floor(sec / 60);
  const seconds = Math.round(sec % 60);
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
}

/**
 * Unified Activity Timeline
 *
 * One timeline for every notes surface (deal page, contact drawer, workspace).
 * Single query over crm_activities scoped to the contact AND their deals, so
 * contact notes appear on deal views and deal notes appear on contact views —
 * each clearly badged with its origin.
 *
 * Features:
 * - Category / source / author filter bar (default: show everything)
 * - Origin badges: "Contact note" (teal) vs deal-name (purple); on deal views
 *   (primaryDealId set) the deal's own rows stay unbadged
 * - AI call summaries (type call_summary) render summary-first with a lazy
 *   "Show transcript" expander backed by crm_dialpad_calls
 * - Grouped by date, color-coded, expandable email bodies, real pagination
 */
export default function UnifiedActivityTimeline({
  contactId,
  dealIds = [],
  maxHeight = '600px',
  initialLimit = 50,
  onEmailAction,
  refreshKey = 0,
  primaryDealId = null,
  presetCategories = null,
}) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [pages, setPages] = useState(1);
  const [expandedEmails, setExpandedEmails] = useState({});
  const [selectedCategories, setSelectedCategories] = useState(
    () => new Set(presetCategories && presetCategories.length ? presetCategories : ALL_CATEGORY_KEYS)
  );
  const [selectedSources, setSelectedSources] = useState(() => new Set(ALL_SOURCE_KEYS));
  const [authorFilter, setAuthorFilter] = useState('all');
  const toast = useToast();

  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const timelineBG = useColorModeValue('gray.50', 'gray.900');
  const cardBG = useColorModeValue('white', 'gray.700');
  const lineColor = useColorModeValue('gray.300', 'gray.600');
  const emailBodyBg = useColorModeValue('white', 'gray.600');
  const systemNoteBG = useColorModeValue('blue.50', 'blue.900');
  const systemNoteBorder = useColorModeValue('blue.200', 'blue.700');

  const loadActivities = useCallback(async (pageCount = 1, { silent = false } = {}) => {
    if (!silent) setLoading(true);

    try {
      const fetchLimit = initialLimit * pageCount;
      let query = supabase
        .from('crm_activities')
        .select('*, crm_deals!deal_id(deal_name)')
        .order('created_at', { ascending: false })
        .limit(fetchLimit + 1); // +1 row to detect whether more exist

      if (dealIds.length > 0 && contactId) {
        query = query.or(
          `deal_id.in.(${dealIds.join(',')}),contact_id.eq.${contactId}`
        );
      } else if (dealIds.length > 0) {
        // Deal without a linked contact — deal-scoped only
        query = query.in('deal_id', dealIds);
      } else {
        query = query.eq('contact_id', contactId);
      }

      const { data, error } = await query;

      if (error) throw error;

      const rows = data || [];
      setHasMore(rows.length > fetchLimit);

      const allActivities = rows.slice(0, fetchLimit).map(activity => {
        const meta = activity.metadata || {};
        const source = !activity.deal_id
          ? 'contact'
          : (primaryDealId && activity.deal_id === primaryDealId ? 'primary' : 'deal');
        return {
          ...meta,
          id: activity.id,
          dealId: activity.deal_id,
          contactId: activity.contact_id,
          dealName: activity.crm_deals?.deal_name || (activity.deal_id ? 'Unknown Deal' : null),
          text: activity.content,
          authorId: activity.author_id,
          authorName: activity.author_name || meta.authorName || 'Unknown',
          authorPhotoURL: meta.authorPhotoURL || '',
          timestamp: activity.created_at ? new Date(activity.created_at) : null,
          activityType: meta.activityType || activity.type || 'update',
          type: activity.type,
          disposition: meta.disposition || '',
          isSystemNote: activity.author_id === 'system',
          source,
          metadata: meta,
        };
      });

      setActivities(allActivities);
      setLoading(false);
    } catch (error) {
      console.error('Error loading unified activity timeline:', error);
      setLoading(false);
    }
  }, [contactId, dealIds, initialLimit, primaryDealId]);

  useEffect(() => {
    if (!contactId && dealIds.length === 0) {
      setActivities([]);
      setLoading(false);
      return;
    }

    setPages(1);
    loadActivities(1);
    // refreshKey lets parents force a refetch (e.g. after logging an activity)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId, dealIds, refreshKey]);

  const handleLoadMore = async () => {
    const nextPages = pages + 1;
    setPages(nextPages);
    setLoadingMore(true);
    await loadActivities(nextPages, { silent: true });
    setLoadingMore(false);
  };

  const categoryCounts = useMemo(() => {
    const counts = {};
    activities.forEach((activity) => {
      const cat = categoryForType(activity.type || activity.activityType);
      counts[cat] = (counts[cat] || 0) + 1;
    });
    return counts;
  }, [activities]);

  const authors = useMemo(() => {
    const names = new Set();
    activities.forEach((a) => names.add(a.authorName || 'Unknown'));
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [activities]);

  const sourceOptions = useMemo(() => {
    const present = new Set(activities.map((a) => a.source));
    const opts = [];
    if (present.has('primary')) opts.push({ key: 'primary', label: 'This deal' });
    if (present.has('deal')) opts.push({ key: 'deal', label: primaryDealId ? 'Other deals' : 'Deals' });
    if (present.has('contact')) opts.push({ key: 'contact', label: 'Contact-level' });
    // Only worth showing when there is actually a mix to filter
    return opts.length > 1 ? opts : [];
  }, [activities, primaryDealId]);

  const filteredActivities = useMemo(() => {
    return activities.filter((activity) => {
      const cat = categoryForType(activity.type || activity.activityType);
      if (!selectedCategories.has(cat)) return false;
      if (!selectedSources.has(activity.source)) return false;
      if (authorFilter !== 'all' && (activity.authorName || 'Unknown') !== authorFilter) return false;
      return true;
    });
  }, [activities, selectedCategories, selectedSources, authorFilter]);

  const groupedActivities = useMemo(() => {
    return filteredActivities.reduce((groups, activity) => {
      const timestamp = moment(activity.timestamp);

      const dateKey = timestamp.isValid() ? timestamp.format('YYYY-MM-DD') : 'unknown';

      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }

      groups[dateKey].push(activity);
      return groups;
    }, {});
  }, [filteredActivities]);

  const isFiltered =
    selectedCategories.size < ALL_CATEGORY_KEYS.length ||
    selectedSources.size < ALL_SOURCE_KEYS.length ||
    authorFilter !== 'all';

  const toggleCategory = (key) => {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSource = (key) => {
    setSelectedSources((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const resetFilters = () => {
    setSelectedCategories(new Set(ALL_CATEGORY_KEYS));
    setSelectedSources(new Set(ALL_SOURCE_KEYS));
    setAuthorFilter('all');
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const time = moment(timestamp);
    return time.isValid() ? time.format('h:mm A') : '';
  };

  const getRelativeTime = (timestamp) => {
    if (!timestamp) return '';
    const time = moment(timestamp);
    return time.isValid() ? time.fromNow() : '';
  };

  const getActivityIcon = (activity) => {
    const activityType = activity.activityType || activity.type || 'update';
    if (ACTIVITY_ICONS[activityType]) return ACTIVITY_ICONS[activityType];
    if (activity.isSystemNote) return FiInfo;
    return FiAlertCircle;
  };

  const getActivityColor = (activity) => {
    const activityType = activity.activityType || activity.type || 'update';
    if (ACTIVITY_COLORS[activityType]) return ACTIVITY_COLORS[activityType];
    if (activity.isSystemNote) return 'purple';
    return 'gray';
  };

  const toggleEmailExpanded = (activityId) => {
    setExpandedEmails(prev => ({
      ...prev,
      [activityId]: !prev[activityId],
    }));
  };

  const copyEmailToClipboard = (activity) => {
    const subject = activity.subject || '(No Subject)';
    const body = stripHtml(activity.body || '');
    const to = activity.to || '';
    const formattedEmail = `Subject: ${subject}\nTo: ${to}\n\n${body}`;
    navigator.clipboard.writeText(formattedEmail);
    toast({
      title: 'Copied to clipboard',
      description: 'Email content copied successfully',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  if (loading) {
    return (
      <Center py={12}>
        <VStack>
          <Spinner size="lg" />
          <Text>Loading activity timeline...</Text>
        </VStack>
      </Center>
    );
  }

  if (activities.length === 0) {
    return (
      <Box textAlign="center" py={12}>
        <Icon as={FiMessageSquare} boxSize={12} color="gray.300" mb={3} />
        <Text color="gray.500">No activity yet</Text>
        <Text fontSize="sm" color="gray.400">
          Activity from this contact and all associated deals will appear here
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      {/* Filter Controls */}
      <VStack align="stretch" spacing={2} mb={4}>
        <Wrap spacing={1.5}>
          {ACTIVITY_CATEGORIES.map((cat) => {
            const count = categoryCounts[cat.key] || 0;
            if (count === 0) return null;
            const isActive = selectedCategories.has(cat.key);
            return (
              <WrapItem key={cat.key}>
                <Button
                  size="xs"
                  borderRadius="full"
                  variant={isActive ? 'solid' : 'outline'}
                  colorScheme={isActive ? 'blue' : 'gray'}
                  opacity={isActive ? 1 : 0.7}
                  onClick={() => toggleCategory(cat.key)}
                >
                  {cat.label} ({count})
                </Button>
              </WrapItem>
            );
          })}
        </Wrap>

        <HStack spacing={2} flexWrap="wrap">
          {sourceOptions.map((opt) => {
            const isActive = selectedSources.has(opt.key);
            return (
              <Button
                key={opt.key}
                size="xs"
                variant={isActive ? 'solid' : 'outline'}
                colorScheme={isActive ? (opt.key === 'contact' ? 'teal' : 'purple') : 'gray'}
                opacity={isActive ? 1 : 0.7}
                onClick={() => toggleSource(opt.key)}
              >
                {opt.label}
              </Button>
            );
          })}

          {authors.length > 1 && (
            <Select
              size="xs"
              maxW="180px"
              value={authorFilter}
              onChange={(e) => setAuthorFilter(e.target.value)}
            >
              <option value="all">All users</option>
              {authors.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </Select>
          )}

          {isFiltered && (
            <Button size="xs" variant="link" colorScheme="blue" onClick={resetFilters}>
              Reset filters
            </Button>
          )}

          <Badge colorScheme={isFiltered ? 'blue' : 'gray'} variant="subtle">
            Showing {filteredActivities.length} of {activities.length}{hasMore ? '+' : ''}
          </Badge>
        </HStack>
      </VStack>

      {/* Timeline */}
      <Box
        bg={timelineBG}
        p={4}
        borderRadius="lg"
        borderWidth="1px"
        borderColor={borderColor}
        maxH={maxHeight}
        overflowY="auto"
        css={{
          '&::-webkit-scrollbar': { width: '8px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: '#CBD5E0', borderRadius: '4px' },
          '&::-webkit-scrollbar-thumb:hover': { background: '#A0AEC0' },
        }}
      >
        <VStack align="stretch" spacing={6}>
          {Object.entries(groupedActivities).sort(([dateA], [dateB]) => dateB.localeCompare(dateA)).map(([dateKey, dateActivities]) => {
            const date = moment(dateKey);
            const isToday = date.isSame(moment(), 'day');
            const isYesterday = date.isSame(moment().subtract(1, 'day'), 'day');

            let dateLabel = date.format('MMM DD, YYYY');
            if (isToday) dateLabel = 'Today';
            else if (isYesterday) dateLabel = 'Yesterday';

            return (
              <Box key={dateKey}>
                {/* Date Header */}
                <HStack mb={4}>
                  <Divider flex={1} borderColor={lineColor} />
                  <Badge
                    colorScheme={isToday ? 'blue' : 'gray'}
                    fontSize="sm"
                    px={3}
                    py={1}
                    borderRadius="full"
                  >
                    {dateLabel}
                  </Badge>
                  <Divider flex={1} borderColor={lineColor} />
                </HStack>

                {/* Timeline Items */}
                <VStack align="stretch" spacing={3} position="relative" pl={6}>
                  {/* Vertical Line */}
                  <Box
                    position="absolute"
                    left="17px"
                    top="0"
                    bottom="0"
                    width="2px"
                    bg={lineColor}
                  />

                  {dateActivities.map((activity) => {
                    const ActivityIcon = getActivityIcon(activity);
                    const activityColor = getActivityColor(activity);
                    const isCallSummary = (activity.type === 'call_summary' || activity.activityType === 'call_summary');
                    const isNoteLike = isCallSummary || activity.type === 'note';
                    const callDuration = isCallSummary ? formatCallDuration(activity.duration) : null;

                    return (
                      <HStack key={activity.id} align="flex-start" spacing={0} position="relative">
                        {/* Timeline Icon */}
                        <Box
                          position="absolute"
                          left="-26px"
                          top="0"
                          zIndex={1}
                        >
                          <Tooltip label={isCallSummary ? 'AI call summary' : (activity.activityType || activity.type || 'Activity')}>
                            <Box
                              bg={`${activityColor}.100`}
                              borderWidth="2px"
                              borderColor={`${activityColor}.500`}
                              borderRadius="full"
                              p={2}
                            >
                              <Icon
                                as={ActivityIcon}
                                boxSize={4}
                                color={`${activityColor}.600`}
                              />
                            </Box>
                          </Tooltip>
                        </Box>

                        {/* Activity Card */}
                        <Box
                          flex={1}
                          bg={activity.isSystemNote ? systemNoteBG : cardBG}
                          p={3}
                          borderRadius="md"
                          borderWidth="1px"
                          borderColor={activity.isSystemNote ? systemNoteBorder : borderColor}
                          shadow="sm"
                          transition="all 0.2s"
                          _hover={{ shadow: 'md', borderColor: `${activityColor}.300` }}
                        >
                          <VStack align="stretch" spacing={2}>
                            {/* Header */}
                            <HStack justify="space-between" align="flex-start">
                              <HStack spacing={2} flex={1}>
                                <Avatar
                                  size="xs"
                                  name={activity.authorName}
                                  src={activity.authorPhotoURL}
                                />
                                <VStack align="flex-start" spacing={0}>
                                  <HStack>
                                    <Text fontWeight="bold" fontSize="sm">
                                      {activity.authorName || 'Unknown'}
                                    </Text>
                                    {isCallSummary ? (
                                      <Badge colorScheme="cyan" fontSize="xs">AI SUMMARY</Badge>
                                    ) : activity.isSystemNote && (
                                      <Badge colorScheme="purple" fontSize="xs">SYSTEM</Badge>
                                    )}
                                  </HStack>
                                  {activity.disposition && (
                                    <Badge colorScheme="blue" fontSize="xs">
                                      {activity.disposition.replace(/-/g, ' ')}
                                    </Badge>
                                  )}
                                  {isInboundSmsActivity(activity) && (
                                    <Badge colorScheme="purple" fontSize="xs">Received</Badge>
                                  )}
                                </VStack>
                              </HStack>

                              <VStack align="flex-end" spacing={0}>
                                <Text fontSize="xs" color="gray.500" fontWeight="medium">
                                  {formatTimestamp(activity.timestamp)}
                                </Text>
                                <Text fontSize="2xs" color="gray.400">
                                  {getRelativeTime(activity.timestamp)}
                                </Text>
                              </VStack>
                            </HStack>

                            {/* Origin badges — clearly mark where this entry lives */}
                            {activity.source === 'contact' ? (
                              <Badge size="xs" colorScheme="teal" w="fit-content">
                                {isNoteLike ? 'Contact note' : 'Contact'}
                              </Badge>
                            ) : activity.source === 'deal' && activity.dealName ? (
                              <Badge size="xs" colorScheme="purple" w="fit-content">
                                {activity.dealName}
                              </Badge>
                            ) : null}

                            {/* Call summary context (direction, duration, LO) */}
                            {isCallSummary && (
                              <HStack spacing={2} flexWrap="wrap">
                                {activity.direction && (
                                  <Badge colorScheme={activity.direction === 'outbound' ? 'blue' : 'purple'} fontSize="2xs">
                                    {activity.direction === 'outbound' ? 'Outbound call' : 'Inbound call'}
                                  </Badge>
                                )}
                                {callDuration && (
                                  <Badge colorScheme="gray" fontSize="2xs">{callDuration}</Badge>
                                )}
                                {activity.loName && (
                                  <Badge colorScheme="cyan" variant="subtle" fontSize="2xs">
                                    {activity.loName}
                                  </Badge>
                                )}
                              </HStack>
                            )}

                            {/* Activity Text (for call summaries this is the AI summary, shown first) */}
                            {activity.text && (
                              <Text fontSize="sm" whiteSpace="pre-wrap">
                                {activity.text}
                              </Text>
                            )}

                            {activity.description && !activity.text && (
                              <Text fontSize="sm" color="gray.600">
                                {activity.description}
                              </Text>
                            )}

                            {/* Expandable raw transcript, lazy-loaded from crm_dialpad_calls */}
                            {isCallSummary && (
                              <TranscriptDisclosure
                                callRowId={activity.callRowId}
                                dialpadCallId={activity.dialpadCallId}
                              />
                            )}

                            {/* Changes List */}
                            {activity.changes && activity.changes.length > 0 && (
                              <VStack align="stretch" spacing={1} mt={1}>
                                {activity.changes.map((change, idx) => (
                                  <HStack key={idx} fontSize="xs" color="gray.600" pl={2}>
                                    <Box
                                      w="4px"
                                      h="4px"
                                      borderRadius="full"
                                      bg={`${activityColor}.400`}
                                    />
                                    <Text>{change}</Text>
                                  </HStack>
                                ))}
                              </VStack>
                            )}

                            {/* Email Activity Details */}
                            {(activity.activityType === 'email_sent' || activity.type === 'email_sent') && (activity.subject || activity.body) && (
                              <Box
                                mt={2}
                                p={3}
                                bg="purple.50"
                                borderRadius="md"
                                borderLeftWidth="3px"
                                borderLeftColor="purple.400"
                              >
                                <VStack align="stretch" spacing={2}>
                                  <HStack justify="space-between" align="flex-start">
                                    <VStack align="stretch" spacing={1} flex={1}>
                                      {activity.subject && (
                                        <HStack spacing={2}>
                                          <Icon as={FiMail} boxSize={3} color="purple.500" flexShrink={0} />
                                          <Text fontSize="sm" fontWeight="semibold" color="purple.800">
                                            {activity.subject}
                                          </Text>
                                        </HStack>
                                      )}
                                      {activity.to && (
                                        <Text fontSize="xs" color="gray.600" pl={5}>
                                          To: {activity.to}
                                        </Text>
                                      )}
                                      {activity.sentAsEmail && (
                                        <Badge colorScheme="orange" fontSize="2xs" alignSelf="flex-start" ml={5}>
                                          Sent as: {activity.sentAsUserName || activity.sentAsEmail}
                                        </Badge>
                                      )}
                                    </VStack>

                                    <Tooltip label="Copy email to clipboard">
                                      <IconButton
                                        icon={<FiCopy />}
                                        size="sm"
                                        variant="ghost"
                                        colorScheme="purple"
                                        aria-label="Copy email"
                                        onClick={() => copyEmailToClipboard(activity)}
                                      />
                                    </Tooltip>
                                    {onEmailAction && (
                                      <>
                                        <Tooltip label="Reply">
                                          <IconButton
                                            icon={<FiCornerUpLeft />}
                                            size="sm"
                                            variant="ghost"
                                            aria-label="Reply"
                                            onClick={() => onEmailAction('reply', activity)}
                                          />
                                        </Tooltip>
                                        <Tooltip label="Forward">
                                          <IconButton
                                            icon={<FiCornerUpRight />}
                                            size="sm"
                                            variant="ghost"
                                            aria-label="Forward"
                                            onClick={() => onEmailAction('forward', activity)}
                                          />
                                        </Tooltip>
                                      </>
                                    )}
                                  </HStack>

                                  <EmailTrackingBadge activityId={activity.id} />

                                  {activity.body && (
                                    <>
                                      <Button
                                        size="xs"
                                        variant="ghost"
                                        colorScheme="purple"
                                        leftIcon={expandedEmails[activity.id] ? <FiChevronUp /> : <FiChevronDown />}
                                        onClick={() => toggleEmailExpanded(activity.id)}
                                        alignSelf="flex-start"
                                      >
                                        {expandedEmails[activity.id] ? 'Hide email body' : 'Show email body'}
                                      </Button>

                                      <Collapse in={expandedEmails[activity.id]} animateOpacity>
                                        <Box
                                          mt={2}
                                          p={3}
                                          bg={emailBodyBg}
                                          borderRadius="md"
                                          borderWidth="1px"
                                          borderColor="purple.200"
                                          maxH="300px"
                                          overflowY="auto"
                                          css={{
                                            '&::-webkit-scrollbar': { width: '6px' },
                                            '&::-webkit-scrollbar-thumb': { background: '#9F7AEA', borderRadius: '3px' },
                                          }}
                                        >
                                          <Text
                                            fontSize="sm"
                                            whiteSpace="pre-wrap"
                                            color="gray.700"
                                            dangerouslySetInnerHTML={{
                                              __html: DOMPurify.sanitize(activity.body || '')
                                            }}
                                          />
                                        </Box>
                                      </Collapse>
                                    </>
                                  )}
                                </VStack>
                              </Box>
                            )}

                            {/* Stage Change Indicator */}
                            {activity.activityType === 'stage_change' && activity.previousStage && activity.newStage && (
                              <HStack spacing={2} mt={2} fontSize="sm">
                                <Badge colorScheme="gray">
                                  {STAGES.find(s => s.value === activity.previousStage)?.label || activity.previousStage}
                                </Badge>
                                <Text>→</Text>
                                <Badge colorScheme="green">
                                  {STAGES.find(s => s.value === activity.newStage)?.label || activity.newStage}
                                </Badge>
                              </HStack>
                            )}
                          </VStack>
                        </Box>
                      </HStack>
                    );
                  })}
                </VStack>
              </Box>
            );
          })}
        </VStack>

        {/* Load More */}
        {hasMore && (
          <Box textAlign="center" mt={6}>
            <Button
              size="sm"
              variant="outline"
              onClick={handleLoadMore}
              isLoading={loadingMore}
            >
              Load More Activity
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}
